import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ShieldCheck, BadgeCheck, Star, MapPin, Loader2, CheckCircle2, Phone, Mail, Heart,
} from 'lucide-react';
import { getRef } from '../lib/attribution';
import { Seo } from '../components/seo/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MicroAgencyPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: '', contact_email: '', contact_phone: '', care_need: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/agency/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Please enter your name'); return; }
    if (!form.contact_email && !form.contact_phone) { toast.error('Add an email or phone so we can reach you'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/agency/${slug}/lead`, { ...form, ref: getRef() || 'clinician_share' });
      setSent(true);
      toast.success('Request sent — they will reach out soon');
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Could not send request');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-cn-bg"><Loader2 className="w-8 h-8 animate-spin text-cn-primary" /></div>;
  if (notFound || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cn-bg px-4 text-center" data-testid="agency-not-found">
        <h1 className="cn-display text-2xl text-cn-text">Page not found</h1>
        <p className="text-cn-text-muted mt-2">This practice page isn't available.</p>
        <a href="/" className="cn-btn cn-btn-primary mt-5">Go to CaraLoom</a>
      </div>
    );
  }

  const brand = data.brand_color || '#0F172A';
  const c = data.clinician || {};
  const initials = (c.name || data.business_name || 'C').split(' ').map((n) => n[0]).join('').slice(0, 2);

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="micro-agency-page" style={{ '--brand': brand }}>
      <Seo
        title={`${data.business_name} — Private Home Care${c.city ? ` in ${c.city}, ${c.state}` : ''} | CaraLoom`}
        description={(data.tagline || data.about || c.bio || `Book trusted, licensed home care with ${data.business_name} on CaraLoom.`).slice(0, 160)}
        path={`/c/${slug}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'MedicalBusiness',
          name: data.business_name,
          description: data.tagline || data.about || undefined,
          ...(c.city ? { address: { '@type': 'PostalAddress', addressLocality: c.city, addressRegion: c.state, addressCountry: 'US' } } : {}),
        }}
      />
      {/* Hero */}
      <header className="relative overflow-hidden text-white" style={{ background: brand }}>
        <div className="max-w-5xl mx-auto px-6 py-14 sm:py-20">
          <div className="flex items-center gap-5 flex-wrap">
            <div className="w-24 h-24 rounded-3xl overflow-hidden flex-shrink-0 bg-white/20 flex items-center justify-center text-3xl font-display font-bold">
              {c.photo_url ? <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" /> : initials}
            </div>
            <div>
              <h1 className="font-display font-bold text-3xl sm:text-4xl text-white" data-testid="agency-name">{data.business_name}</h1>
              <p className="text-white/85 text-base sm:text-lg mt-1">{data.tagline}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3 text-sm">
                {c.license_type && <span className="bg-white/15 rounded-full px-3 py-1">{c.license_type}{c.years_of_experience ? ` · ${c.years_of_experience} yrs` : ''}</span>}
                {(c.city) && <span className="inline-flex items-center gap-1 bg-white/15 rounded-full px-3 py-1"><MapPin className="w-3.5 h-3.5" /> {c.city}, {c.state}</span>}
                {c.license_verified && <span className="inline-flex items-center gap-1 bg-white/15 rounded-full px-3 py-1"><ShieldCheck className="w-3.5 h-3.5" /> License verified</span>}
                {c.npi_verified && <span className="inline-flex items-center gap-1 bg-white/15 rounded-full px-3 py-1"><BadgeCheck className="w-3.5 h-3.5" /> NPI verified</span>}
                {c.rating > 0 && <span className="inline-flex items-center gap-1 bg-white/15 rounded-full px-3 py-1"><Star className="w-3.5 h-3.5" /> {c.rating} ({c.review_count})</span>}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-5 gap-8">
        {/* About + services */}
        <div className="lg:col-span-3 space-y-8">
          {(data.about || c.bio) && (
            <section data-testid="agency-about">
              <h2 className="cn-display text-xl text-cn-text mb-2">About</h2>
              <p className="text-cn-text leading-relaxed">{data.about || c.bio}</p>
            </section>
          )}
          {c.specialties?.length > 0 && (
            <section>
              <h2 className="cn-display text-xl text-cn-text mb-3">Specialties</h2>
              <div className="flex flex-wrap gap-2">
                {c.specialties.map((s) => <span key={s} className="text-sm bg-cn-bone-200 text-cn-text px-3 py-1 rounded-full">{s}</span>)}
              </div>
            </section>
          )}
          {data.services?.length > 0 && (
            <section data-testid="agency-services">
              <h2 className="cn-display text-xl text-cn-text mb-3">Services & rates</h2>
              <div className="space-y-2">
                {data.services.map((sv, i) => (
                  <div key={i} className="flex items-center justify-between cn-card rounded-xl px-4 py-3" data-testid={`service-${i}`}>
                    <span className="text-cn-text font-medium">{sv.name}</span>
                    {sv.rate != null && <span className="font-display font-bold" style={{ color: brand }}>${sv.rate}<span className="text-xs text-cn-text-muted font-normal">/{sv.unit || 'visit'}</span></span>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Intake form */}
        <div className="lg:col-span-2">
          <div className="cn-card rounded-2xl p-6 sticky top-6" data-testid="agency-intake">
            {sent ? (
              <div className="text-center py-6" data-testid="agency-intake-success">
                <CheckCircle2 className="w-14 h-14 mx-auto mb-3" style={{ color: brand }} />
                <h3 className="cn-display text-xl text-cn-text">Request sent</h3>
                <p className="text-sm text-cn-text-muted mt-1">{c.name || 'The care team'} will reach out shortly.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Heart className="w-5 h-5" style={{ color: brand }} />
                  <h3 className="cn-display text-xl text-cn-text">Request care</h3>
                </div>
                <p className="text-sm text-cn-text-muted mb-4">{data.accepting_clients ? 'Accepting new clients' : 'Send a message'} — no account needed.</p>
                <form onSubmit={submit} className="space-y-3">
                  <div>
                    <Label className="text-xs">Your name *</Label>
                    <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="intake-name" />
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label className="text-xs">Phone</Label>
                      <Input value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} placeholder="(555) 555-5555" data-testid="intake-phone" />
                    </div>
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} data-testid="intake-email" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">What do you need help with?</Label>
                    <Input value={form.care_need} onChange={(e) => setForm((f) => ({ ...f, care_need: e.target.value }))} placeholder="e.g. wound care after surgery" data-testid="intake-need" />
                  </div>
                  <div>
                    <Label className="text-xs">Message</Label>
                    <Textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} rows={3} data-testid="intake-message" />
                  </div>
                  <button type="submit" disabled={submitting} className="cn-btn w-full justify-center !py-3 text-white" style={{ background: brand }} data-testid="intake-submit">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                    Request care
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="border-t border-cn-border py-6 text-center text-xs text-cn-text-muted">
        Powered by <a href="/" className="font-semibold text-cn-primary">CaraLoom</a> · Independent provider. CaraLoom is a technology marketplace, not a care provider.
      </footer>
    </div>
  );
}
